/**
 * Shared helpers used by gamesSlice / savedPlays / transformedPlaysData
 * across QB, ST and (via createGamesSlice) any practice app that opts into
 * server-synced games. Pure functions only — no redux, no API calls.
 *
 * Extracted from QB's gamesSlice so all apps get one canonical version of
 * the "is this a server-backed game" / "which game is the active one" /
 * "how do I normalize a remote play into local shape" logic.
 */

export const CURRENT_GAME_LABEL = "current game";

/**
 * Server-assigned game_ids are numeric strings ("42"). Locally-created
 * unsaved games use UUIDs which are NOT numeric. This distinction is used
 * everywhere the slice picks between "the temp game I'm building right now"
 * and "the game the server owns".
 */
export function isServerBackedId(
  value?: string | number | null,
): boolean {
  if (value === undefined || value === null) return false;
  // Guard against Number("")===0 / Number("  ")===0 / "0": an empty, blank, or
  // zero id is NOT a server-assigned game_id (Postgres serials start at 1).
  // Without this, a malformed/empty game_id would be treated as the server's
  // current game and plays could attach to a nonexistent game.
  const str = String(value).trim();
  if (str === "") return false;
  const parsed = Number(str);
  return Number.isFinite(parsed) && parsed > 0;
}

/**
 * Loose game shape used by the helpers. Real slice types can extend this
 * without breaking the util — only the two fields we read are required.
 */
export type GameLike = {
  game_id: string | number;
  game_description?: string;
  complete?: boolean;
};

/**
 * Find the game that should be treated as "the current game" for input:
 * the server-backed row whose description is literally "current game" OR
 * whose `complete` is false. Locally-created unsaved games are skipped so
 * we don't dispatch new plays into a game the server hasn't seen yet.
 */
export function pickCurrentGameCandidate<G extends GameLike>(
  games: G[] | null | undefined,
): G | undefined {
  return games?.find((game) => {
    if (!isServerBackedId(game?.game_id)) return false;
    const description = String(game.game_description ?? "").trim().toLowerCase();
    return description === CURRENT_GAME_LABEL || game.complete === false;
  });
}

/**
 * Turn any object into a string safely. Used when normalizing remote play
 * data — the backend sometimes returns numbers or nulls where the local
 * PlayData interface expects strings.
 */
export function coerceString(value: unknown): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value;
  return String(value);
}

/**
 * Merge remote play_data into a local empty scaffold. Keys defined in
 * `emptyPlayData` are coerced to string; unknown keys are copied through
 * verbatim (so app-specific fields survive the round trip).
 *
 * Each app provides its own `emptyPlayData` — QB has `quarterback,
 * throwType, ...`, ST adds special-teams timing fields, etc.
 */
export function normalizeRemotePlayData<T extends Record<string, unknown>>(
  raw: Record<string, unknown> | undefined,
  emptyPlayData: () => T,
): T {
  const normalized = emptyPlayData();
  if (!raw) return normalized;

  (Object.keys(normalized) as Array<keyof T>).forEach((key) => {
    if (raw[key as string] !== undefined) {
      (normalized[key] as unknown) = coerceString(raw[key as string]);
    }
  });

  for (const [key, value] of Object.entries(raw)) {
    if (!(key in normalized)) {
      (normalized as Record<string, unknown>)[key] = value;
    }
  }

  return normalized;
}

/**
 * Strip the app-side metadata a play carries locally (isDeleted,
 * needsSync, syncNeeded) before POSTing to the backend. The server owns
 * `play_id` and `play_data`; everything else is UI state.
 */
export function sanitizePlayForUpload<T extends Record<string, unknown>>(
  play: T,
): Record<string, unknown> {
  const { isDeleted, needsSync, syncNeeded, ...rest } = play as Record<
    string,
    unknown
  > & {
    isDeleted?: unknown;
    needsSync?: unknown;
    syncNeeded?: unknown;
  };
  void isDeleted;
  void needsSync;
  void syncNeeded;
  return rest;
}
